/**
 * The rows of TrackSummaryCard, in the order the work happens. Kept apart from
 * the card so they can be read in a test without the stores or the server.
 */
import type { PopoverRow, PopoverRowState } from '@/components/ds';
import { RankMark } from '@/components/prioritization/RankMark';
import type { ReadinessCondition, TrackReadinessStatus } from '@/lib/trackReadinessEvaluator';
import type { PublishedTrackResult, TrackResultRecord } from '@/lib/tracks/publishedTrackResult';
import { formatDate } from '@/i18n/formatters';

/** What the page already knows about the track, in words it has already formatted. */
export interface TrackSummaryBasics {
  trackId: string;
  href: string;
  ownerName: string | null;
  /** Null: a standalone track, in no cycle. */
  cycleName: string | null;
  pool: { name: string; itemCount: number; lockedOn: string | null } | null;
  /** The criteria's names; null when the page does not hold them (the readiness check then speaks for that row). */
  criteria: string[] | null;
  /** The day each part was locked or agreed, already formatted ("3 Oct"); empty when not. */
  lockedOn: { participation: string; criteria: string; scales: string; values: string };
}

/** What the card reads from the server. `undefined` = still reading; `null` = could not be read. */
export interface TrackSummaryServerFacts {
  readiness: TrackReadinessStatus | null | undefined;
  published: PublishedTrackResult | null | undefined;
  /** The newest track_results row (for this item's place in it). */
  record: TrackResultRecord | null | undefined;
}

export interface TrackSessionCounts {
  total: number;
  ranked: number;
}

const plural = (n: number, w: string) => `${n} ${w}${n === 1 ? '' : 's'}`;
const sentence = (s: string) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

/** Ready = done; something there but not locked or accepted = partly; nothing yet = not yet. */
function stepState(c: ReadinessCondition): PopoverRowState {
  if (c.ready) return 'done';
  return c.blockers.length > 0 && c.blockers.every((b) => /not locked|not accepted/i.test(b)) ? 'partial' : 'open';
}

function stepRow(
  label: string,
  readiness: TrackReadinessStatus | null | undefined,
  pick: (r: TrackReadinessStatus) => ReadinessCondition,
  whenReady: string,
  value?: string,
): PopoverRow {
  if (readiness === undefined) return { label, value: value ?? 'Checking…' };
  if (readiness === null) return { label, value: value ?? 'Not known', note: value ? undefined : 'Readiness could not be checked.' };
  const c = pick(readiness);
  const blocker = c.blockers[0];
  return {
    label,
    state: stepState(c),
    value: value ?? (c.ready ? whenReady : (blocker ?? 'Not ready')),
    note: value ? (c.ready ? whenReady : blocker) : undefined,
  };
}

/** The card's rows. */
export function trackSummaryRows(
  basics: TrackSummaryBasics,
  sessions: TrackSessionCounts,
  server: TrackSummaryServerFacts,
  itemId?: string,
): PopoverRow[] {
  const { readiness, published, record } = server;
  const locked = (on: string, word = 'Locked') => (on ? `${word} ${on}` : 'Ready');
  const rows: PopoverRow[] = [
    { id: 'owner', label: 'Owner', value: basics.ownerName ?? 'No owner named' },
    { id: 'participants', ...stepRow('Participants', readiness, (r) => r.s1_participation, locked(basics.lockedOn.participation)) },
    {
      id: 'pool',
      ...stepRow(
        'Item pool',
        readiness,
        (r) => r.s2_pool,
        locked(basics.pool?.lockedOn ?? ''),
        basics.pool ? `${basics.pool.name} · ${plural(basics.pool.itemCount, 'item')}` : 'No pool yet',
      ),
    },
    {
      id: 'criteria',
      ...stepRow(
        'Criteria',
        readiness,
        (r) => r.s3_criteria,
        locked(basics.lockedOn.criteria),
        basics.criteria === null ? undefined : basics.criteria.length ? basics.criteria.join(', ') : 'None chosen yet',
      ),
    },
    { id: 'scales', ...stepRow('Value scales', readiness, (r) => r.s4_scales, locked(basics.lockedOn.scales, 'Accepted')) },
    { id: 'values', ...stepRow('Values', readiness, (r) => r.s5_values, locked(basics.lockedOn.values, 'Agreed')) },
    {
      id: 'sessions',
      label: 'Sessions',
      state: sessions.ranked > 0 ? 'done' : sessions.total > 0 ? 'partial' : 'open',
      value: sessions.total === 0 ? 'None yet' : `${plural(sessions.total, 'session')} · ${sessions.ranked} ranked`,
    },
  ];

  if (published === undefined) {
    rows.push({ id: 'published', label: 'Published result', value: 'Checking…' });
  } else if (published === null) {
    rows.push({ id: 'published', label: 'Published result', value: 'Could not be read' });
  } else if (!published.trackResultId) {
    rows.push({ id: 'published', label: 'Published result', state: 'open', value: 'None yet', note: published.reason ? `${sentence(published.reason)}.` : undefined });
  } else {
    const by = [published.publishedByName ? `By ${published.publishedByName}` : null, published.publishedAt ? formatDate(new Date(published.publishedAt), { day: 'numeric', month: 'short' }) : null]
      .filter(Boolean)
      .join(', ');
    const waiting = published.waitingCount > 0 ? `${plural(published.waitingCount, 'closed session')} newer than this wait on the person who holds the ball.` : null;
    rows.push({
      id: 'published',
      label: 'Published result',
      state: 'done',
      value: published.strategy === 'aggregate' ? `Combined from ${plural(published.sourceCount, 'session')}` : (published.sessionName ?? 'Untitled session'),
      note: [by, waiting].filter(Boolean).join(' · ') || undefined,
    });
    if (itemId && record !== undefined) {
      const mine = record?.items.find((x) => x.itemId === itemId);
      const cut = record?.cutLinePosition ?? null;
      rows.push({
        id: 'this-item',
        label: 'This item',
        value: mine ? (
          <span className="inline-flex items-baseline gap-1.5">
            <RankMark rank={mine.rank} official={false} aboveTheLine={cut == null ? null : mine.rank <= cut} size="sm" />
            <span>of {record?.itemCount ?? published.itemCount}</span>
            {cut != null && <span className="text-[var(--text-dim)]">· {mine.rank <= cut ? 'above' : 'below'} the cut</span>}
          </span>
        ) : record ? (
          'Not in this result'
        ) : (
          'Could not be read'
        ),
      });
    }
  }
  return rows;
}

